import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaUser, FaLock, FaEnvelope } from 'react-icons/fa';
import { FcGoogle } from 'react-icons/fc';
import { Link } from 'react-router-dom';
import { fetchWithAuth } from '../services/api';
import {
  consumeIntendedDestination,
  getLanguageFromPath,
  hasValidAuthentication,
} from '../services/auth';

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState('general'); // 'general' | 'unverified'

  // Redirect if already logged in
  React.useEffect(() => {
    if (hasValidAuthentication()) {
      const lang = getLanguageFromPath();
      navigate(consumeIntendedDestination() || `/${lang}`, { replace: true });
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrorType('general');

    try {
      const data = await fetchWithAuth('/auth/login', {
        method: 'POST',
        body: formData,
        credentials: 'include',
        isPublic: true,
      });

      if (data.success && data.accessToken && data.userEmail && data.userFullName && data.role) {
        localStorage.setItem('jwtToken', data.accessToken);
        localStorage.setItem('userEmail', data.userEmail);
        localStorage.setItem('userFullName', data.userFullName);
        localStorage.setItem('userRole', data.role);
        
        // Set flag to indicate fresh login - this will be used by Navbar to apply user's default language
        localStorage.setItem('freshLogin', 'true');
        
        window.dispatchEvent(new Event("storage")); 

        const lang = getLanguageFromPath();
        navigate(consumeIntendedDestination() || `/${lang}`, { replace: true });
      } else {
        if (data.message && data.message.includes('Please verify your email')) {
          setError(t('emailVerification.emailNotVerified'));
          setErrorType('unverified');
        } else {
          setError(data.message || t('login.error.invalid'));
          setErrorType('general');
        }
      }
    } catch (err) {
      if (err.message && err.message.includes('Authentication failed')) {
        setError(t('login.error.invalidCredentials'));
        setErrorType('general');
      } else if (err.message && err.message.includes('Please verify your email')) {
        setError(t('emailVerification.emailNotVerified'));
        setErrorType('unverified');
      } else {
        setError(t('login.error.network'));
        setErrorType('general');
      }
    }
  };

  const handleGoogleLogin = () => {
    // Google login functionality to be implemented
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-gray-50">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {t('login.title')}
        </h2>
        <div className="mt-6 text-center">
          <p className="text-base text-gray-700 mb-3">
            {t('login.noAccount')}
          </p>
          <Link 
            to={`/${window.location.pathname.split('/')[1] || 'mk'}/register`} 
            className="inline-flex items-center justify-center px-8 py-3 text-lg font-bold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            {t('login.registerHere')}
          </Link>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-lg sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t('login.email')}
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center justify-center pointer-events-none">
                  <FaUser className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  {t('login.password')}
                </label>
                <Link 
                  to={`/${window.location.pathname.split('/')[1] || 'mk'}/forgot-password`}
                  className="text-sm text-blue-600 hover:text-blue-500"
                >
                  {t('login.forgotPassword', 'Forgot password?')}
                </Link>
              </div>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center justify-center pointer-events-none">
                  <FaLock className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  data-testid="login-password-input"
                />
              </div>
            </div>

            {error && (
              <div
                className={`text-sm font-medium flex items-start gap-2 p-3 rounded-md ${
                  errorType === 'unverified'
                    ? 'text-rose-700 bg-rose-50 border border-rose-200'
                    : 'text-red-600 bg-red-50 border border-red-200'
                }`}
                data-testid="login-error-message"
              >
                {errorType === 'unverified' && (
                  <FaEnvelope className="h-4 w-4 mt-0.5 shrink-0 text-rose-500" />
                )}
                <span>{error}</span>
              </div>
            )}

            <div>
              <button
                type="submit"
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                data-testid="login-submit-button"
              >
                {t('login.submit')}
              </button>
            </div>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">
                    или
                  </span>
                </div>
              </div>

              <div className="mt-6">
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  disabled
                  className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-400 bg-gray-100 cursor-not-allowed opacity-60"
                >
                  <FcGoogle className="h-5 w-5 mr-2 opacity-50" />
                  {t('login.googleLogin')}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
